import { RWSService, RWSInject } from "@rws-framework/client";
import { calculateDaysDifferenceFromNow, formatDateMMDDYYYY, startsInFuture} from "../helpers/dateUtils";
import StorageService, { StorageServiceInstance } from "src/services/StorageService";
import TimeFormatService, { TimeFormatServiceInstance } from "src/services/TimeFormatService";
import type { StudentCourseType } from "src/components/programs/student-courses/types";
import type { Payment, Course } from "src/components/programs/typesRaw";
import type { Course as CourseLooselyTyped } from "src/types/course";

import type { JunctionModal } from "src/components/modal/component";

type Badge = {
  text: string,
  color: string
}

type PaymentStatus =
  | 'free'
  | 'trial'
  | 'paid'
  | 'payment_required'
  | 'end'
  | 'refunded'
  | 'ia_opt_out';

// Backend API types for GET /payment/course_info/{course_id}/ (get_payment_course_info)
type PaymentCourseInfo = {
  status: PaymentStatus;
  stripe_public_key?: string;
  payment?: Partial<Payment>;
  payments?: unknown[];
  purchase_date?: string;
  payment_type?: "Card";
  credit?: boolean;
  dontshowfreetrialendedmessage?: boolean;
  'paid-error'?: string;
  errno?: string;
};

type AdditionalCoursePaymentData = {credit?: boolean; force_modal?: boolean, is_free?: boolean};
type CourseDataObject = (Course | CourseLooselyTyped) & AdditionalCoursePaymentData;

//TODO Not sure if we need this, if frontend will get all of this fro backend
class CoursePaymentsService extends RWSService {
  static _IN_CLIENT: boolean = true;

  constructor(
    @StorageService private storageService: StorageServiceInstance,
    @TimeFormatService private timeService: TimeFormatServiceInstance,
  ) {
    super();
  }

  static DISPUTED_STATUSES = ['disputed', 'dispute_created', 'dispute_funds_withdrawn'];

  private canRenew(course: Pick<Course & Payment, 'enrollment_locked' | 'course_renewable'>): boolean {
    return !!course.course_renewable && course.enrollment_locked !== true;
  }

  public shouldBuy(course, isProfessor: boolean): string {
    if (isProfessor || course.is_free || course.amount === 0 || course.payment_status === 'free') {
      return '';
    };

    const canRenew = this.canRenew(course);

    if (!canRenew && (course.paid || course.payment_status === 'paid')) return '';

    if (CoursePaymentsService.DISPUTED_STATUSES.includes(course.payment_status)) {
      // Disputed re-payment must go through Stripe so the dispute penalty can be
      // applied — access codes are not allowed.
      if (course.nocreditcard) return '';
      return 'Disputed';
    }

    const opensInFuture = startsInFuture(course.open_date);
    if (!opensInFuture && course.end_date) {
      const diffDays = calculateDaysDifferenceFromNow(course.end_date);
      if (diffDays < 0 && !canRenew) return '';
      if (diffDays < 0 && canRenew) return course.nocreditcard ? 'Redeem code' : (course.isbn_id ? 'Redeem Code or Buy Now' : 'Buy Now');
    }

    //need to pay by date
    if (course.payment_status === 'trial' || course.payment_status === 'payment_required' || course.payment_possible) {
      const diffDays = calculateDaysDifferenceFromNow(course.pay_by_date);
      if (diffDays > 0 || course.payment_possible) {
        if (course.nocreditcard) return 'Redeem code';
        if (course.isbn_id) return 'Redeem Code or Buy Now';
        return 'Buy Now';
      }
    };

    return 'Redeem Code or Buy Now';
  }

  public getBadge(course): Badge[] | [] {
    const status = []
    const opensInFuture = startsInFuture(course.open_date);

    if (opensInFuture) {
      status.push({
        text: 'Opens on ' + formatDateMMDDYYYY(course.open_date),
        color: 'green'
      });
    }

    if (course.use_enrollment_date) {
      if (!course.paid && !course.is_free && (course.payment_status === 'trial' || course.payment_status === 'payment_required')) {
        const diffDays = calculateDaysDifferenceFromNow(course.pay_by_date);
        status.push({
          text: 'Payment Required' +(diffDays > 0 ? ` in ${diffDays} days` : ''),
          color: 'yellow',
        });

        return status;
      }

      if (course.payment_status === 'refunded') {
        status.push({
          text: 'Refunded',
          color: 'red',
        })
        return status;
      };

      if (CoursePaymentsService.DISPUTED_STATUSES.includes(course.payment_status)) {
        status.push({
          text: 'Charge Disputed',
          color: 'red',
        })
        return status;
      };
    };

    if (!opensInFuture && course.end_date && !course.is_free) {
      const diffDays = calculateDaysDifferenceFromNow(course.end_date);
      const paid = course.paid || course.payment_status === 'paid';
      if (diffDays > 0) {
        status.push({
          text: (paid ? 'Paid ': '') + 'Access Ends on ' + formatDateMMDDYYYY(course.end_date),
          color: 'green',
        })
      } else {
        status.push({
          text: (paid ? 'Paid ': '') + 'Access Ended on ' + formatDateMMDDYYYY(course.end_date),
          color: 'red',
        })
      }
    };
    return status;
  }

  public async purchase(course_id: string, bundleId: string = undefined) {
    const csrfToken = Cookies.get('csrf_token');
    const APIprefix = window.edrnaConfig.host + window.edrnaConfig.serverPrefix;
    const currentLocation = window.location.hash.replace('#', '');
    const payload = bundleId ? {location: 'list', cancel: `list/bundle/${bundleId}`} : {location: currentLocation};
    try {
      // payment:checkout:session
      const response = await fetch(`${APIprefix}checkout/${course_id}`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const session = await response.json();
      if (session.error) {
        const error_message= `Error: ${session.error}`;
        return error_message;
      }
      window.sessionStorage.setItem('checkout_session_id', session.id);
      window.location.assign(session.url);
    } catch (error) {
      const error_message = "An error occurred during checkout.";
      console.error("Checkout failed:", error);
      return error_message;
    }
  }

  public async checkPayments(data) {
    const csrfToken = Cookies.get('csrf_token');
    const paymentInfoUrl = window.edrnaConfig.host + window.edrnaConfig.serverPrefix + 'payment/course_info/' + data._id + '/';
    try {
      const response = await fetch(paymentInfoUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });
      let statusData: PaymentCourseInfo & { data?: PaymentCourseInfo } = await response.json();

      if (typeof statusData.data === 'object') statusData = statusData.data;
      if (statusData.errno == 'not-logged') return;
      if (statusData.credit) {
        // flush my-courses-cache
        this.storageService.set("prop", "dashboard", undefined)
      }
      if (data.force_modal && statusData.status === "free") {
        statusData.status = "trial";
      }

      return statusData;
    } catch (error) {
      console.error(error);
      throw error;
    };
  };

  public hasCourseAccessEnded(): Promise<boolean> {
    return this.storageService.getAsync('course', { courseId: window.stateParams.courseId })
      .then((course) =>
        this.checkPayments(course).then((payment) =>
          this.accessHasEnded(payment?.status, course.end_date),
        ),
      )
      .catch(() => false);
  }

  accessHasEnded (status: PaymentStatus | undefined, endDate?: string): boolean {
    return status === 'end' || (status === 'refunded' && new Date(endDate) < new Date());
  }

  //from angular payments.service
  public async checkCoursePayments(data: CourseDataObject) {
    const course_payment = await this.checkPayments(data);
    if (['paid', 'free'].includes(course_payment.status)) {
      if (data.credit) {
        document.documentElement.dispatchEvent(
          new CustomEvent('jnct:notifications:success', {detail: 'Good news! We applied a credit for this course!'})
        );
      }
      return;
    }
    this.setPaymentTemplate(data as Course & Payment & AdditionalCoursePaymentData, course_payment);
  };

  private setPaymentTemplate(courseData: Course & Payment & AdditionalCoursePaymentData, course_payment: PaymentCourseInfo) {
    const paymentStatus = course_payment.status;
    const payment = course_payment.payment;
    const diffDays = calculateDaysDifferenceFromNow(courseData.end_date);

    const pastEndDate = diffDays < 0;
    const refundedAndPastEndDate = paymentStatus === 'refunded' && pastEndDate;
    const accessEnded = paymentStatus === 'end';
    const showFinishedModal = !this.canRenew(courseData) && (refundedAndPastEndDate || accessEnded);

    if (showFinishedModal) {
      //show end modal
      const modal = document.createElement('jnct-modal') as JunctionModal;
      modal.templateName = "course-finished-modal";
      modal.genericConfig = {
        labelBundle: window.labelBundle,
        course: courseData,
        switch: () => {
          document.documentElement.dispatchEvent(
            new CustomEvent('jnct:navigation:gotolist')
          );
          modal.remove();
        }
      }
      document.body.appendChild(modal);
      return;
    };

    const paymentData: StudentCourseType = {
      _id: courseData._id,
      collegeLogoUrl: courseData.collegeLogoUrl,
      course_entry: courseData.course_entry,
      title: courseData.title,
      tags: courseData.tags,
      pay_by_date: courseData.pay_by_date,
      end_date: courseData.end_date,
      is_free: courseData.is_free,
      open_date: courseData.open_date,
      payment_status: course_payment.status,
      paid: false,
      isbn: courseData.ISBN,
      publicKey: course_payment.stripe_public_key,
      payAmount: (courseData.amount / 100).toFixed(2),
      stripePayAmount: courseData.amount,
      nocreditcard: payment.nocreditcard,
      course_renewable: courseData.course_renewable,
      enrollment_locked: courseData.enrollment_locked,
      dontshowfreetrialendedmessage: course_payment.dontshowfreetrialendedmessage,
      previousFailure: course_payment['paid-error']
    };

    // If no isbn then can only pay with credit card so we go directly to stripe
    if (!paymentData.isbn && paymentStatus !== 'trial' && paymentStatus !== 'end') {
      this.purchase(courseData._id);
      return;
    }

    //show modal once per session storage during courtesy period
    const location = window.location.href;
    const flag = 'seenPurchaseModal_' + courseData._id;
    //show once per session and only inside the course
    if (sessionStorage.getItem(flag) == 'true' && paymentData.payment_status === 'trial' && !location.includes('list')) {
      return;
    }
    const modal = document.createElement('jnct-modal') as JunctionModal;
    modal.templateName = "course-purchase-modal";
    modal.genericConfig = {
      labelBundle: window.labelBundle,
      course: paymentData,
      switch: () => {
        if (!paymentData) {
          document.documentElement.dispatchEvent(
            new CustomEvent('jnct:navigation:gotolist')
          );
          modal.remove();
          return;
        }

        const location = window.location.href;
        if (paymentData.payment_status === 'trial') {
          //show once per session and only inside the course
          if (location.includes('list')) {
            modal.remove();
            return;
          }
          const flag = 'seenPurchaseModal_' + paymentData._id;
          sessionStorage.setItem(flag, 'true');
          modal.remove();
          return;
        };
        document.documentElement.dispatchEvent(
          new CustomEvent('jnct:navigation:gotolist')
        );

        modal.remove()
      },
      updateStatus: (data) => {
        this.storageService.get("prop", "dashboard", (dashboard) => {
          if (dashboard?.assigned?.length) {
            const assignedCourse = dashboard.assigned.find(course => course._id === courseData._id);
            if (assignedCourse) {
              assignedCourse.payment_possible = false;
              this.storageService.set("prop", "dashboard", dashboard);
            }
          }
        });
      }
    }

    document.body.appendChild(modal);
  };

  public getPaymentMessage(status: string, course: StudentCourseType) {
    if (course.previousFailure) {
      return `Your last transaction on ${this.timeService.longDateTime(course.previousFailure)} was not successful. Please try again.`;
    };
    switch (status) {
      case 'trial':
        const daysLeft = calculateDaysDifferenceFromNow(course.pay_by_date);
        if (daysLeft === 0) return 'Courtesy access ends today.';
        if (daysLeft === 1) return 'Courtesy access ends tomorrow.';
        if (daysLeft > 1) return `Courtesy access ends in ${daysLeft} days.`;
        return 'Courtesy access has ended.';
        break;
      case 'payment_required':
        if (course.dontshowfreetrialendedmessage) return 'Courtesy access ended. Payment required.';
        return `Courtesy access ended on ${this.timeService.longDateTime(course.pay_by_date)}.`;
        break;
      case 'refunded':
        return 'We have refunded your prior payment. If you have reconsidered, go ahead and enter your payment information.';
        break;
      case 'ia_opt_out':
        return 'You have opted out of your school’s institutional purchase program.  A payment is now required to access the courseware.';
        break;
      case 'disputed':
      case 'dispute_created':
      case 'dispute_funds_withdrawn':
        if (course.nocreditcard) return 'A prior charge was disputed. There is no alternative payment method available.';

        return window.labelBundle?.dispute_penalty_payment_label
          || 'A prior charge was disputed. You may pay again — a dispute penalty may be added to your purchase.';
      case 'end':
        return this.canRenew(course) ? 'Your course access has ended. Purchase the course again to renew your access.' : 'Your course access has ended.';
        break;
      case '':
        return '';
        break;
      default:
        return `Something went horribly wrong. Please contact ${window.labelBundle.mailto}.`;
    }
  };
}

export default CoursePaymentsService.getSingleton();

export { CoursePaymentsService as CoursePaymentsServiceInstance };
